/**
 * Centralized numeric formatting helpers.
 *
 * All display-layer code should call these functions instead of using
 * toLocaleString / toFixed directly. Each function accepts a resolved
 * config object produced by buildFormattingConfig().
 */

/**
 * Build a resolved config by merging clientData.decimalsConfig over defaults.
 * Call once after clientData is loaded and store the result in AppState.formattingConfig.
 *
 * @param  {object} [decimalsConfig]  — clientData.decimalsConfig (may be undefined)
 * @returns {object}                  — resolved config with all keys present
 */
export function buildFormattingConfig(decimalsConfig = {}) {
	const toInt = (v, def) => {
		if (v === null || v === undefined || v === '') return def;
		const n = Number(v);
		return Number.isFinite(n) ? Math.trunc(n) : def;
	};
	return {
		currency: toInt(decimalsConfig.currency, 0),
		cents: toInt(decimalsConfig.cents, 2),
		number: toInt(decimalsConfig.number, 0),
		percentage: toInt(decimalsConfig.percentage, 2),
		multiplier: toInt(decimalsConfig.multiplier, 2),
		sharePrices: toInt(decimalsConfig.sharePrices, 4),
		ownershipPercentage: toInt(decimalsConfig.ownershipPercentage, 2),
	};
}

/**
 * Format a dollar amount.
 *
 * @param {number}  value
 * @param {object}  config        — resolved config from buildFormattingConfig()
 * @param {boolean} [useCents]    — true → use config.cents precision instead of config.currency
 * @returns {string}
 */
export function formatCurrency(value, config, useCents = false) {
	if (typeof value !== 'number' || isNaN(value)) return '$0';
	const decimals = useCents ? config.cents : config.currency;
	return (
		'$' +
		value.toLocaleString('en-US', {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		})
	);
}

/**
 * Format a plain number (share counts, units, etc.).
 *
 * @param {number} value
 * @param {object} config
 * @returns {string}
 */
export function formatNumber(value, config) {
	if (typeof value !== 'number' || isNaN(value)) return '0';
	return value.toLocaleString('en-US', {
		minimumFractionDigits: config.number,
		maximumFractionDigits: config.number,
	});
}

/**
 * Format a value that is already in percent form (e.g. 12.5 → "12.50%").
 *
 * @param {number} value  — percent value (12.5, not 0.125)
 * @param {object} config
 * @returns {string}
 */
export function formatPercentage(value, config) {
	if (typeof value !== 'number' || isNaN(value)) return '0%';
	return (
		value.toLocaleString('en-US', {
			minimumFractionDigits: config.percentage,
			maximumFractionDigits: config.percentage,
		}) + '%'
	);
}

/**
 * Format a ratio (0–1) as a percentage string (e.g. 0.125 → "12.50%").
 *
 * @param {number} value  — ratio value (0.125, not 12.5)
 * @param {object} config
 * @returns {string}
 */
export function formatPercentageFromRatio(value, config) {
	if (typeof value !== 'number' || isNaN(value)) return '0%';
	return formatPercentage(value * 100, config);
}

/**
 * Format a multiplier value (TVPI, MOIC, DPI).
 *
 * @param {number} value
 * @param {object} config
 * @returns {string}
 */
export function formatMultiplier(value, config) {
	if (typeof value !== 'number' || isNaN(value)) return '0.00x';
	return (
		value.toLocaleString('en-US', {
			minimumFractionDigits: config.multiplier,
			maximumFractionDigits: config.multiplier,
		}) + 'x'
	);
}

/**
 * Format a per-share price using sharePrices precision.
 *
 * @param {number} value
 * @param {object} config
 * @returns {string}
 */
export function formatSharePrice(value, config) {
	if (typeof value !== 'number' || isNaN(value)) return '$0';
	return (
		'$' +
		value.toLocaleString('en-US', {
			minimumFractionDigits: config.sharePrices,
			maximumFractionDigits: config.sharePrices,
		})
	);
}

/**
 * Generic dispatcher matching the type vocabulary used in HeadlineFormatter
 * and EquityCardFormatter. Pass formatOverride to select cents vs dollars.
 *
 * Percentage values passed through this dispatcher are expected to be ratios
 * (0–1), matching the convention used by the calculation engine.
 *
 * @param {number} value
 * @param {string} type           — 'currency' | 'number' | 'percentage' | 'multiplier' | 'sharePrice'
 * @param {object} config
 * @param {string} [formatOverride] — 'cents' to use cents precision for currency
 * @returns {string}
 */
export function formatByType(value, type, config, formatOverride = null) {
	switch (type) {
		case 'currency':
			return formatCurrency(value, config, formatOverride === 'cents');
		case 'number':
			return formatNumber(value, config);
		case 'percentage':
			return formatPercentageFromRatio(value, config);
		case 'multiplier':
			return formatMultiplier(value, config);
		case 'sharePrice':
			return formatSharePrice(value, config);
		default:
			return String(value ?? '');
	}
}
